/**
 * Tab-System Context für Multi-Window Arbeiten
 * Ermöglicht das Öffnen mehrerer Seiten in Tabs
 */

import { createContext, useContext, useState, useCallback, useRef } from 'react';

const TabContext = createContext(null);

// Tab-Definitionen mit Titel-Generierung
const TAB_CONFIGS = {
  '/': { title: 'Dashboard', icon: 'dashboard' },
  '/posteingang': { title: 'Posteingang', icon: 'inbox' },
  '/akten': { title: 'Betreuungsakten', icon: 'users' },
  '/personen': { title: 'Personenakten', icon: 'user' },
  '/aufgaben': { title: 'Aufgaben', icon: 'tasks' },
  '/bueroorganisation': { title: 'Büroorganisation', icon: 'briefcase' },
  '/kommunikation': { title: 'Schriftverkehr', icon: 'mail' },
  '/buchhaltung': { title: 'Buchhaltung', icon: 'euro' },
  '/wiki': { title: 'Wiki', icon: 'book' },
  '/ocr': { title: 'OCR', icon: 'file' },
  '/zeo-verlauf': { title: 'Zeo Verlauf', icon: 'message-square' },
  '/admin': { title: 'Administration', icon: 'settings' },
  '/admin/recrawl': { title: 'Re-Crawl', icon: 'refresh' },
  '/admin/profil': { title: 'Mein Profil', icon: 'user' },
  '/institutionen-hierarchie': { title: 'Institutionen-Hierarchie', icon: 'building' },
  '/edv-umzug': { title: 'EDV Umzug', icon: 'download' },
  // Schnellerfassungs-Tabs
  '/schnellerfassung/person': { title: 'Neue Person', icon: 'user' },
  '/schnellerfassung/institution': { title: 'Neue Institution', icon: 'briefcase' },
  '/schnellerfassung/massnahme': { title: 'Neue Massnahme', icon: 'briefcase' },
  '/schnellerfassung/aufgabe': { title: 'Neue Aufgabe', icon: 'tasks' },
  '/schnellerfassung/konto': { title: 'Neues Konto', icon: 'euro' },
  '/schnellerfassung/kontoauszug': { title: 'Kontoauszug Upload', icon: 'file' },
  '/schnellerfassung/csv-import': { title: 'CSV/MT940 Import', icon: 'file' },
  '/schnellerfassung/finapi': { title: 'Bank verbinden', icon: 'euro' },
  '/schnellerfassung/kategorie': { title: 'Neue Kategorie', icon: 'settings' },
  '/schnellerfassung/aufgabenkreis': { title: 'Aufgabenkreis', icon: 'briefcase' },
  '/schnellerfassung/lebenssituation': { title: 'Lebenssituation', icon: 'user' },
  '/schnellerfassung/bericht': { title: 'Neuer Bericht', icon: 'file' },
  '/schnellerfassung/verguetung': { title: 'Vergütungsantrag', icon: 'euro' },
  '/schnellerfassung/recherche': { title: 'Externe Recherche', icon: 'shield' },
  '/schnellerfassung/schriftstueck-senden': { title: 'Schriftstück senden', icon: 'mail' },
};

// Dynamische Titel für Detail-Seiten — mit Seitentyp-Präfix
const getDynamicTitle = (path, params = {}) => {
  const name = params.name || params.title;
  
  if (path.match(/^\/akten\/[^/]+\/blick$/)) {
    return name ? `Blick: ${name}` : 'Akte auf einen Blick';
  }
  if (path.match(/^\/akten\/[^/]+\/uebergabe$/)) {
    return name ? `Übergabe: ${name}` : 'Übergabe';
  }
  if (path.match(/^\/akten\/[^/]+\/vorgang\//)) {
    return name ? `Vorgang: ${name}` : 'Vorgang';
  }
  if (path === '/akten/neu') {
    return 'Neue Akte';
  }
  if (path.match(/^\/akten\/[^/]+\/bearbeiten$/)) {
    return name ? `Bearbeiten: ${name}` : 'Akte bearbeiten';
  }
  if (path.match(/^\/akten\/[^/]+$/)) {
    return name ? `Akte: ${name}` : 'Betreuungsakte';
  }
  if (path.startsWith('/aufgaben/') && path !== '/aufgaben/neu') {
    return name ? `Aufgabe: ${name}` : 'Aufgabe';
  }
  if (path.startsWith('/personen/') || path.startsWith('/personenakten/')) {
    return name ? `Person: ${name}` : 'Person';
  }
  return null;
};

// Tab-Typ für Farbcodierung bestimmen
const getTabType = (path) => {
  if (path === '/') return 'dashboard';
  if (path.match(/^\/akten\/[^/]+\/blick$/)) return 'blick';
  if (path.match(/^\/akten\/[^/]+\/uebergabe$/)) return 'uebergabe';
  if (path.match(/^\/akten\/[^/]+\/vorgang\//)) return 'vorgang';
  if (path.match(/^\/akten\/[^/]+/)) return 'akte';
  if (path === '/akten') return 'akten-liste';
  if (path.startsWith('/aufgaben')) return 'aufgaben';
  if (path.startsWith('/buchhaltung') || path.startsWith('/schulden')) return 'finanzen';
  if (path.startsWith('/kommunikation') || path.startsWith('/korrespondenz')) return 'kommunikation';
  if (path === '/posteingang') return 'posteingang';
  if (path.startsWith('/aktenuebernahme')) return 'aktenuebernahme';
  if (path.startsWith('/personen') || path.startsWith('/personenakten')) return 'personen';
  if (path.startsWith('/admin') || path.startsWith('/buero')) return 'admin';
  return 'default';
};

// Deep-Link (ZEO "oeffne diese Aufgabe", 07.10.2026): ein NEUER Browser-Tab auf /aufgaben/<id> startet mit der kanonischen Aufgabenmaske als aktivem Tab
// (bisher wertete das Tab-System die URL nicht aus und zeigte nur das Dashboard). Bewusst eng: nur Aufgaben-Detailpfade.
export function deepLinkTab(pathname, search = '') {
  // Korrespondenz (25.10.2026; frueher /schreibstube): ZEO/Aufgabe/alte Links -> /korrespondenz?brief=...|klient=...|aufgabe=... im eigenen Browser-Tab
  if (/^\/(schreibstube|korrespondenz)\/?$/.test(pathname || '')) {
    const q = new URLSearchParams(search || '');
    const erlaubt = new URLSearchParams();
    ['brief', 'zeo_vorschlag', 'klient', 'aufgabe', 'aktion'].forEach((k) => { const v = q.get(k); if (v && /^[A-Za-z0-9:_-]{1,80}$/.test(v)) erlaubt.set(k, v); });
    const s = erlaubt.toString();
    return { id: 'tab-2', path: s ? `/korrespondenz?${s}` : '/korrespondenz', title: 'Korrespondenz', icon: 'file', tabType: getTabType('/korrespondenz'), closable: true, params: {} };
  }
  const m = /^\/aufgaben\/([A-Za-z0-9-]{8,64})\/?$/.exec(pathname || '');
  if (!m || m[1] === 'neu') return null;
  return { id: 'tab-2', path: `/aufgaben/${m[1]}`, title: 'Aufgabe', icon: 'file', tabType: getTabType(`/aufgaben/${m[1]}`), closable: true, params: {} };
}

export function TabProvider({ children }) {
  const deepLink = deepLinkTab(typeof window !== 'undefined' ? window.location.pathname : '', typeof window !== 'undefined' ? window.location.search : '');
  // Liste der geöffneten Tabs
  const [tabs, setTabs] = useState([
    { id: 'tab-1', path: '/', title: 'Dashboard', tabType: 'dashboard', closable: false },
    ...(deepLink ? [deepLink] : []),
  ]);
  
  // Aktiver Tab
  const [activeTabId, setActiveTabId] = useState(deepLink ? deepLink.id : 'tab-1');
  
  // Tab-ID Counter (ref statt state für synchronen Zugriff)
  const tabCounterRef = useRef(deepLink ? 3 : 2);
  
  // Ref für aktuelle Tabs (für synchronen Zugriff in Callbacks)
  const tabsRef = useRef(tabs);
  tabsRef.current = tabs;

  // Neuen Tab öffnen (verwendet Refs für konsistenten Zugriff)
  const openTab = useCallback((path, options = {}) => {
    // Backward-Kompatibilität: openTab(path, "Titel") → openTab(path, {title: "Titel"})
    if (typeof options === 'string') {
      options = { title: options };
    }
    const { title, forceNew = false, params = {} } = options;
    path = path.replace(/^\/schreibstube(?=\/?($|\?))/, '/korrespondenz');   // alte Schreibstube-Links: derselbe EINE Arbeitsplatz
    const pathOnly = path.split('?')[0];
    
    // Prüfe ob Tab mit diesem Pfad bereits existiert (über Ref für Aktualität)
    const currentTabs = tabsRef.current;
    const existingTab = !forceNew && currentTabs.find(t => t.path.split('?')[0] === pathOnly);
    if (existingTab) {
      setActiveTabId(existingTab.id);
      // Pfad und Titel updaten
      const newTitle = title || getDynamicTitle(path, params) || existingTab.title;
      if (existingTab.path !== path || existingTab.title !== newTitle) {
        setTabs(prev => prev.map(t => t.id === existingTab.id ? { ...t, path, title: newTitle } : t));
        tabsRef.current = tabsRef.current.map(t => t.id === existingTab.id ? { ...t, path, title: newTitle } : t);
      }
      return existingTab.id;
    }
    
    // Neuen Tab erstellen
    const newTabId = `tab-${tabCounterRef.current}`;
    tabCounterRef.current += 1;
    
    const basePath = path.split('/').slice(0, 2).join('/') || path;
    const config = TAB_CONFIGS[basePath] || TAB_CONFIGS[pathOnly];
    const dynamicTitle = getDynamicTitle(path, params);
    
    const newTab = {
      id: newTabId,
      path: path,
      title: title || dynamicTitle || config?.title || 'Neu',
      icon: config?.icon || 'file',
      tabType: getTabType(path),
      closable: true,
      params: params
    };
    
    // Functional updater mit Deduplizierungs-Prüfung
    setTabs(prev => {
      // Nochmal prüfen ob ein anderer Call bereits diesen Tab erstellt hat
      const alreadyExists = prev.find(t => t.path.split('?')[0] === pathOnly);
      if (alreadyExists && !forceNew) {
        setActiveTabId(alreadyExists.id);
        return prev;
      }
      const newTabs = [...prev, newTab];
      // Ref sofort updaten für nachfolgende Aufrufe im selben Event
      tabsRef.current = newTabs;
      return newTabs;
    });
    setActiveTabId(newTabId);
    
    return newTabId;
  }, []);

  // Tab schließen
  const closeTab = useCallback((tabId) => {
    const currentTabs = tabsRef.current;
    const tab = currentTabs.find(t => t.id === tabId);
    if (!tab || !tab.closable) return;
    
    const tabIndex = currentTabs.findIndex(t => t.id === tabId);
    const newTabs = currentTabs.filter(t => t.id !== tabId);
    
    // Wenn der aktive Tab geschlossen wird, zum vorherigen wechseln
    setActiveTabId(prev => {
      if (prev === tabId) {
        const newActiveIndex = Math.max(0, tabIndex - 1);
        return newTabs[newActiveIndex]?.id || newTabs[0]?.id;
      }
      return prev;
    });
    
    setTabs(newTabs);
  }, []);

  // Tab aktivieren
  const activateTab = useCallback((tabId) => {
    setActiveTabId(tabId);
  }, []);

  // Tab-Titel aktualisieren
  const updateTabTitle = useCallback((tabId, newTitle) => {
    setTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, title: newTitle } : tab
    ));
  }, []);

  // Aktiven Tab-Pfad aktualisieren (für Navigation innerhalb eines Tabs)
  const updateTabPath = useCallback((tabId, newPath) => {
    setTabs(prev => prev.map(tab => 
      tab.id === tabId ? { ...tab, path: newPath } : tab
    ));
  }, []);

  // Aktuellen aktiven Tab holen
  const getActiveTab = useCallback(() => {
    return tabs.find(t => t.id === activeTabId);
  }, [tabs, activeTabId]);

  // Prüfen ob ein Pfad bereits offen ist
  const isPathOpen = useCallback((path) => {
    return tabs.some(t => t.path === path);
  }, [tabs]);

  const value = {
    tabs,
    activeTabId,
    activeTab: getActiveTab(),
    openTab,
    closeTab,
    activateTab,
    updateTabTitle,
    updateTabPath,
    isPathOpen,
  };

  return (
    <TabContext.Provider value={value}>
      {children}
    </TabContext.Provider>
  );
}

export function useTabs() {
  const context = useContext(TabContext);
  if (!context) {
    throw new Error('useTabs must be used within a TabProvider');
  }
  return context;
}

export default TabContext;
